import React, { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Menu, X, LogOut, User, ChevronLeft, ChevronRight, ClipboardList, Users } from 'lucide-react'
import { Link, useLocation } from 'wouter'
import { cn } from '@/lib/utils'
import { MODULE_KEYS } from '@/lib/adminModules'
import { MODULE_ICONS } from '@/lib/moduleIcons'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { logout, user, adminProfile, isSuperAdmin, isAgencyAdmin, hasModule } = useAuth()
  const [location] = useLocation()

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev)
  }

  // Visibility follows the module grants a super admin set for this admin.
  // `hasModule` always returns true for super admins, so they keep everything.
  //
  // This only removes the signpost — every route and endpoint behind it is
  // guarded independently, because a hidden link is not a permission.
  // An agency admin only ever manages their own agency's counselors — every
  // other link (including Dashboard) is hidden, matching the route-level
  // redirect in App.tsx's ProtectedRoute.
  const navigation = isAgencyAdmin
    ? [{ name: 'My Counselors', href: '/agency-counselors', icon: Users }]
    : [
    { name: 'Dashboard', href: '/dashboard', icon: MODULE_ICONS[MODULE_KEYS.DASHBOARD] },
    {
      name: 'Manual',
      href: '/guide',
      icon: MODULE_ICONS[MODULE_KEYS.GUIDE],
      // Always allowed server-side (ALWAYS_ALLOWED_MODULE_KEYS), so this is
      // always false in practice — written explicitly, matching every other
      // entry, so a future change to that guarantee shows up here too.
      is_hidden: !hasModule(MODULE_KEYS.GUIDE),
    },
    {
      name: 'Admin Management',
      href: '/admin-management',
      icon: MODULE_ICONS[MODULE_KEYS.ADMIN_MANAGEMENT],
      is_hidden: !isSuperAdmin,
    },
    {
      name: 'AI Guidelines',
      href: '/ai-guidelines',
      icon: MODULE_ICONS[MODULE_KEYS.AI_GUIDELINES],
      is_hidden: !isSuperAdmin,
    },
    {
      name: 'AI Costs',
      href: '/ai-costs',
      icon: MODULE_ICONS[MODULE_KEYS.AI_COSTS],
      is_hidden: !isSuperAdmin,
    },
    {
      name: 'Agency Management',
      href: '/agencies',
      icon: MODULE_ICONS[MODULE_KEYS.AGENCIES],
      is_hidden: !hasModule(MODULE_KEYS.AGENCIES),
    },
    {
      name: 'Counselor Management',
      href: '/counselors',
      icon: MODULE_ICONS[MODULE_KEYS.COUNSELORS],
      is_hidden: !hasModule(MODULE_KEYS.COUNSELORS),
    },
    {
      name: 'Institution Management',
      href: '/colleges',
      icon: MODULE_ICONS[MODULE_KEYS.COLLEGES],
      is_hidden: !hasModule(MODULE_KEYS.COLLEGES),
    },
    {
      name: 'Student Management',
      href: '/students',
      icon: MODULE_ICONS[MODULE_KEYS.STUDENTS],
      is_hidden: !hasModule(MODULE_KEYS.STUDENTS),
    },
    {
      name: 'Calendar Management',
      href: '/calendar-management',
      icon: MODULE_ICONS[MODULE_KEYS.CALENDAR],
      is_hidden: !hasModule(MODULE_KEYS.CALENDAR),
    },
    {
      name: 'Counselor Resources',
      href: '/counselor-resources',
      icon: MODULE_ICONS[MODULE_KEYS.COUNSELOR_RESOURCES],
      is_hidden: !hasModule(MODULE_KEYS.COUNSELOR_RESOURCES),
    },
    {
      name: 'Content (CMS)',
      href: '/cms',
      icon: MODULE_ICONS[MODULE_KEYS.CMS_COLLECTIONS],
      is_hidden: !hasModule(MODULE_KEYS.CMS_COLLECTIONS),
    },
    {
      name: 'Website Content',
      href: '/website-content',
      icon: MODULE_ICONS[MODULE_KEYS.WEBSITE_CONTENT],
      is_hidden: !hasModule(MODULE_KEYS.WEBSITE_CONTENT),
    },
    {
      name: 'Session History',
      href: '/sessions',
      icon: MODULE_ICONS[MODULE_KEYS.SESSIONS],
      is_hidden: !hasModule(MODULE_KEYS.SESSIONS),
    },
    {
      name: 'AI Chat Sessions',
      href: '/ai-sessions',
      icon: MODULE_ICONS[MODULE_KEYS.AI_SESSIONS],
      is_hidden: !hasModule(MODULE_KEYS.AI_SESSIONS),
    },
    {
      // Stopgap admin view of AI MBA Readiness assessments + generated PDF
      // reports while WhatsApp delivery is pending AiSensy template approval.
      // Reuses the AI_SESSIONS module grant rather than a dedicated one —
      // not yet a first-class permission-gated feature.
      name: 'MBA Readiness',
      href: '/mba-readiness',
      icon: ClipboardList,
      is_hidden: !hasModule(MODULE_KEYS.AI_SESSIONS),
    },
    {
      name: 'Message Templates',
      href: '/templates',
      icon: MODULE_ICONS[MODULE_KEYS.TEMPLATES],
      // Sits above Campaigns because the template library is what a campaign
      // draws from — you pick or create a template before you send anything.
      is_hidden: !hasModule(MODULE_KEYS.TEMPLATES),
    },
    {
      name: 'WhatsApp Campaigns',
      href: '/campaigns',
      icon: MODULE_ICONS[MODULE_KEYS.CAMPAIGNS],
      is_hidden: !hasModule(MODULE_KEYS.CAMPAIGNS),
    },
  ]

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar — a flex column (header / scrollable nav / footer) so the
       * account footer is always a normal flow child instead of absolutely
       * positioned over the nav list once there are enough items to need
       * scrolling (as happened once Content (CMS) pushed the list past one
       * short viewport's height). */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          'w-64',
          isCollapsed && 'lg:w-20',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-between h-16 px-6 border-b border-gray-200',
            isCollapsed && 'lg:justify-center lg:px-2'
          )}
        >
          <div className="flex items-center">
            <h1
              className={cn(
                'text-xl font-bold text-gray-900',
                isCollapsed && 'lg:hidden'
              )}
            >
              Admin Portal
            </h1>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
          <button
            onClick={toggleCollapsed}
            className={cn(
              'hidden lg:flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600',
              isCollapsed && 'absolute -right-3 top-5 bg-white border border-gray-200 shadow-sm'
            )}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto mt-6 px-3">
          <div className="space-y-1">
            {navigation
              .filter((item) => !item.is_hidden)
              .map((item) => {
              const isActive = location === item.href
              return (
                // wouter v3's Link renders its own <a> — no child <a> here,
                // which used to produce an invalid nested <a><a>...</a></a>.
                <Link
                  key={item.name}
                  href={item.href}
                  title={isCollapsed ? item.name : undefined}
                  className={cn(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isCollapsed && 'lg:justify-center lg:px-2',
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5 flex-shrink-0',
                      isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3',
                      isActive ? 'text-white' : 'text-gray-500'
                    )}
                  />
                  <span className={cn(isCollapsed && 'lg:hidden')}>
                    {item.name}
                  </span>
                </Link>
              )
            })}
          </div>
        </nav>

        {/* User info at bottom */}
        <div className="shrink-0 p-4 border-t border-gray-200">
          <div
            className={cn(
              'flex items-center mb-3',
              isCollapsed && 'lg:justify-center'
            )}
          >
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className={cn('ml-3 min-w-0 flex-1', isCollapsed && 'lg:hidden')}>
              <p className="text-sm font-medium text-gray-900 truncate">
                {adminProfile?.name || user?.name || user?.phoneNumber}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {isSuperAdmin ? 'Super Admin' : isAgencyAdmin ? 'Agency Admin' : 'Admin'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : undefined}
            className={cn(
              'w-full flex items-center px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-100 transition-colors',
              isCollapsed && 'lg:justify-center lg:px-2'
            )}
          >
            <LogOut className={cn('h-4 w-4', isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3')} />
            <span className={cn(isCollapsed && 'lg:hidden')}>Logout</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="h-6 w-6 text-gray-500" />
            </button>

            <div className="flex ml-auto items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {adminProfile?.name || user?.name || user?.phoneNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {isSuperAdmin ? 'Super Admin' : isAgencyAdmin ? 'Agency Admin' : 'Admin'}
                </p>
              </div>
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          {/* Extra bottom padding on mobile so the fixed PWA install banner
              (rendered at the app root, bottom-4) never covers the last bit
              of a page's content — most noticeable on long pages like the
              Guide/Manual page. */}
          <div className="container mx-auto px-6 pt-8 pb-24 sm:pb-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
